import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  AppBar,
  Toolbar,
  Badge,
  Stack,
  Link,
  InputAdornment,
  Divider,
  Chip,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import MicIcon from '@mui/icons-material/Mic';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import LaptopIcon from '@mui/icons-material/Laptop';
import MemoryIcon from '@mui/icons-material/Memory';
import BuildIcon from '@mui/icons-material/Build';
import PersonIcon from '@mui/icons-material/Person';
import ArticleIcon from '@mui/icons-material/Article';
import GridViewIcon from '@mui/icons-material/GridView';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import GavelIcon from '@mui/icons-material/Gavel';
import HelpIcon from '@mui/icons-material/Help';

// Import logo and hero image
import logo0 from '../assets/logo0.png';
import smartSearchImage from '../assets/smartsearch.png';

// Styled components
const HeaderAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#000000',
  boxShadow: 'none',
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.drawer + 1,
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
}));

const NavLink = styled(Link)(({ theme }) => ({
  color: '#fff',
  textDecoration: 'none',
  fontWeight: 400,
  fontSize: '0.95rem',
  padding: theme.spacing(1),
  transition: 'all 0.2s ease',
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  '&:hover': {
    color: '#FFD700',
    backgroundColor: 'transparent'
  }
}));

const GoldButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFD700',
  color: '#000000',
  '&:hover': {
    backgroundColor: '#FFC800',
  },
  textTransform: 'none',
  fontWeight: 500,
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  padding: '8px 24px',
  borderRadius: 0
}));

const HeroSection = styled(Box)(({ theme }) => ({
  background: '#000000',
  padding: theme.spacing(12, 0),
  position: 'relative',
  overflow: 'hidden'
}));

const SearchContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  boxShadow: 'none',
  position: 'relative',
  zIndex: 1,
  backgroundColor: '#111111',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 0
}));

const CategoryChip = styled(Chip)(({ theme, active }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: active ? '#FFD700' : 'rgba(255, 255, 255, 0.05)',
  color: active ? '#000000' : 'rgba(255, 255, 255, 0.8)',
  border: 'none',
  fontFamily: '"Inter", sans-serif',
  '&:hover': {
    backgroundColor: active ? '#FFC800' : 'rgba(255, 255, 255, 0.1)'
  }
}));

const QuickLinksContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(6)
}));

const QuickLinkCard = styled(Paper)(({ theme }) => ({
  backgroundColor: '#111111',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 0,
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#1A1A1A',
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
    '& .icon': {
      color: '#FFD700'
    }
  }
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#111111',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  padding: theme.spacing(6, 0, 3),
  marginTop: theme.spacing(8)
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  textDecoration: 'none',
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: '0.9rem',
  transition: 'all 0.2s ease',
  display: 'block',
  marginBottom: theme.spacing(1),
  '&:hover': {
    color: '#FFD700',
    textDecoration: 'none'
  }
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#FFD700'
  }
}));

const SmartSearch = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isVoiceSearching, setIsVoiceSearching] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Sample search suggestions
  const searchSuggestions = [
    { label: 'iPhone screen not responding', category: 'parts' },
    { label: 'Samsung Galaxy battery draining fast', category: 'parts' },
    { label: 'MacBook Pro won\'t turn on', category: 'repair' },
    { label: 'How to replace Dell XPS keyboard', category: 'guide' },
    { label: 'Find expert for water damage repair', category: 'expert' },
    { label: 'Laptop overheating solutions', category: 'repair' },
    { label: 'iPhone charging port replacement', category: 'parts' },
    { label: 'TV display showing lines', category: 'repair' }
  ];
  
  // Search categories
  const categories = [
    { id: 'all', name: 'All Categories', icon: <SearchIcon /> },
    { id: 'parts', name: 'Parts', icon: <MemoryIcon /> },
    { id: 'repair', name: 'Repair Solutions', icon: <BuildIcon /> },
    { id: 'guide', name: 'Guides', icon: <ArticleIcon /> },
    { id: 'expert', name: 'Experts', icon: <PersonIcon /> },
    { id: 'device', name: 'Devices', icon: <SmartphoneIcon /> }
  ];

  const handleVoiceSearch = () => {
    setIsVoiceSearching(true);
    setTimeout(() => {
      setSearchQuery('iPhone screen replacement');
      setIsVoiceSearching(false);
    }, 2000);
  };
  
  const handleSearch = () => {
    if (searchQuery) {
      setIsSearching(true);
      // Simulate search process
      setTimeout(() => {
        setIsSearching(false);
        // Here you would typically navigate to results or update state
        console.log(`Searching for: ${searchQuery} in category: ${selectedCategory}`);
      }, 1500);
    }
  };
  
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  return (
    <>
      {/* Header */}
      <HeaderAppBar>
        <Toolbar>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {/* Logo and Navigation */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Box 
                  component="img" 
                  src={logo0} 
                  alt="Warrili Logo"
                  sx={{ 
                    height: 40, 
                    cursor: 'pointer',
                    filter: 'brightness(0) invert(1)' // Makes the logo white
                  }}
                  onClick={() => navigate('/')}
                />
                <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
                  <NavLink href="/parts">Parts</NavLink>
                  <NavLink href="/repair-guide">Repair Guide</NavLink>
                  <NavLink href="/issues">Community</NavLink>
                  <NavLink href="/about">About</NavLink>
                </Stack>
              </Box>

              {/* Right Side Actions */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GoldButton
                  variant="contained"
                  startIcon={<AddCircleIcon />}
                  onClick={() => navigate('/issues')}
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  Post Problem
                </GoldButton>
                
                <IconButton sx={{ color: 'white' }}>
                  <Badge badgeContent={3} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
                
                <IconButton sx={{ color: 'white' }}>
                  <AccountCircleIcon />
                </IconButton>
              </Box>
            </Box>
          </Container>
        </Toolbar>
      </HeaderAppBar>

      {/* Main Content */}
      <Box sx={{ bgcolor: '#000000', minHeight: '100vh', color: '#fff' }}>
        <Container maxWidth="lg">
          {/* Hero Section */}
          <HeroSection>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="h2" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700,
                    color: '#fff',
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2
                  }}
                >
                  Empowering Smart Repairs with AI
                </Typography>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    mb: 4
                  }}
                >
                  Our AI-powered search helps you find the exact parts, repair solutions, guides, and experts for your device issues.
                </Typography>
                
                {/* Enhanced Search Bar */}
                <SearchContainer elevation={3}>
                  <Typography 
                    variant="subtitle1" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      mb: 2
                    }}
                  >
                    What are you looking for today?
                  </Typography>
                  
                  {/* Category Selection */}
                  <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {categories.map((category) => (
                      <CategoryChip
                        key={category.id}
                        label={category.name}
                        icon={category.icon}
                        active={selectedCategory === category.id}
                        onClick={() => handleCategoryChange(category.id)}
                        clickable
                      />
                    ))}
                  </Box>
                  
                  {/* Autocomplete Search */}
                  <Autocomplete
                    freeSolo
                    options={searchSuggestions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ fontFamily: '"Inter", sans-serif' }}>
                        {option.category === 'parts' && <MemoryIcon fontSize="small" sx={{ mr: 1, color: '#FFD700' }} />}
                        {option.category === 'repair' && <BuildIcon fontSize="small" sx={{ mr: 1, color: '#FFD700' }} />}
                        {option.category === 'guide' && <ArticleIcon fontSize="small" sx={{ mr: 1, color: '#FFD700' }} />}
                        {option.category === 'expert' && <PersonIcon fontSize="small" sx={{ mr: 1, color: '#FFD700' }} />}
                        {option.label}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        variant="outlined"
                        placeholder="Search for parts, repair solutions, guides, or experts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                          mb: 3,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#1A1A1A',
                            borderRadius: 0,
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                            },
                            '&:hover fieldset': {
                              borderColor: '#FFD700',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#FFD700',
                            }
                          },
                          '& .MuiInputBase-input': {
                            color: '#fff',
                            fontFamily: '"Inter", sans-serif',
                          },
                          '& .MuiInputAdornment-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton 
                                onClick={handleVoiceSearch}
                                sx={{ 
                                  color: isVoiceSearching ? '#FFD700' : 'rgba(255, 255, 255, 0.7)',
                                  animation: isVoiceSearching ? 'pulse 1.5s infinite' : 'none',
                                  '@keyframes pulse': {
                                    '0%': { opacity: 1 },
                                    '50%': { opacity: 0.5 },
                                    '100%': { opacity: 1 }
                                  }
                                }}
                              >
                                <MicIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    )}
                  />
                  
                  <GoldButton 
                    variant="contained" 
                    size="large"
                    startIcon={isSearching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    onClick={handleSearch}
                    disabled={isSearching || !searchQuery}
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    {isSearching ? 'Searching...' : 'Search with AI'}
                  </GoldButton>
                  
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      textAlign: 'center', 
                      mt: 2,
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: '"Inter", sans-serif'
                    }}
                  >
                    Try: "iPhone screen not responding" or "MacBook Pro won't turn on"
                  </Typography>
                </SearchContainer>
              </Grid>
              
              <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Box 
                  sx={{ 
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {/* Decorative element */}
                  <Box 
                    sx={{ 
                      width: 400,
                      height: 400,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(255,215,0,0.1) 0%, rgba(0,0,0,0) 70%)',
                      position: 'absolute'
                    }}
                  />
                  
                  {/* Hero image */}
                  <Box 
                    component="img"
                    src={smartSearchImage}
                    alt="AI Smart Search"
                    sx={{ 
                      maxWidth: '100%',
                      height: 'auto',
                      position: 'relative',
                      zIndex: 1
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </HeroSection>
          
          {/* Our Mission Section */}
          <Box sx={{ mt: 10, mb: 6 }}>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                color: '#fff',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 3
              }}
            >
              Intelligent Search Across All Categories
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: '"Inter", sans-serif',
                fontSize: '1.1rem',
                lineHeight: 1.7,
                maxWidth: '800px'
              }}
            >
              Our AI-powered search engine understands the context of your query and searches across our entire platform. Whether you're looking for replacement parts, repair solutions, step-by-step guides, or expert technicians, our smart search delivers the most relevant results to solve your device issues.
            </Typography>
          </Box>
          
          {/* Quick Links */}
          <QuickLinksContainer>
            <Typography 
              variant="h4" 
              sx={{ 
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                mb: 4,
                color: '#fff',
                textAlign: 'center'
              }}
            >
              Quick Links
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={6} sm={4} md={2}>
                <QuickLinkCard component={Link} href="/parts">
                  <GridViewIcon 
                    className="icon"
                    sx={{ 
                      fontSize: 48, 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      mb: 2,
                      transition: 'color 0.3s ease'
                    }} 
                  />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 500
                    }}
                  >
                    Hardware
                  </Typography>
                </QuickLinkCard>
              </Grid>
              
              <Grid item xs={6} sm={4} md={2}>
                <QuickLinkCard component={Link} href="/repair-guide">
                  <MenuBookIcon 
                    className="icon"
                    sx={{ 
                      fontSize: 48, 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      mb: 2,
                      transition: 'color 0.3s ease'
                    }} 
                  />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 500
                    }}
                  >
                    Guidelines
                  </Typography>
                </QuickLinkCard>
              </Grid>
              
              <Grid item xs={6} sm={4} md={2}>
                <QuickLinkCard component={Link} href="/contact">
                  <ContactSupportIcon 
                    className="icon"
                    sx={{ 
                      fontSize: 48, 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      mb: 2,
                      transition: 'color 0.3s ease'
                    }} 
                  />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 500
                    }}
                  >
                    Contact
                  </Typography>
                </QuickLinkCard>
              </Grid>
              
              <Grid item xs={6} sm={4} md={2}>
                <QuickLinkCard component={Link} href="/privacy">
                  <PrivacyTipIcon 
                    className="icon"
                    sx={{ 
                      fontSize: 48, 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      mb: 2,
                      transition: 'color 0.3s ease'
                    }} 
                  />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 500
                    }}
                  >
                    Privacy
                  </Typography>
                </QuickLinkCard>
              </Grid>
              
              <Grid item xs={6} sm={4} md={2}>
                <QuickLinkCard component={Link} href="/terms">
                  <GavelIcon 
                    className="icon"
                    sx={{ 
                      fontSize: 48, 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      mb: 2,
                      transition: 'color 0.3s ease'
                    }} 
                  />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 500
                    }}
                  >
                    Terms
                  </Typography>
                </QuickLinkCard>
              </Grid>
              
              <Grid item xs={6} sm={4} md={2}>
                <QuickLinkCard component={Link} href="/support">
                  <HelpIcon 
                    className="icon"
                    sx={{ 
                      fontSize: 48, 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      mb: 2,
                      transition: 'color 0.3s ease'
                    }} 
                  />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 500
                    }}
                  >
                    Support
                  </Typography>
                </QuickLinkCard>
              </Grid>
            </Grid>
          </QuickLinksContainer>
        </Container>
      </Box>

      {/* Footer */}
      <FooterContainer>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="space-between">
            {/* Company Info */}
            <Grid item xs={12} md={4}>
              <Box 
                component="img" 
                src={logo0} 
                alt="Warrili Logo"
                sx={{ 
                  height: 40, 
                  mb: 2,
                  filter: 'brightness(0) invert(1)' // Makes the logo white
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontFamily: '"Inter", sans-serif',
                  mb: 3,
                  maxWidth: '300px'
                }}
              >
                Your trusted platform for device diagnostics and repairs powered by artificial intelligence.
              </Typography>
              
              <Stack direction="row" spacing={1}>
                <SocialIconButton size="small">
                  <FacebookIcon fontSize="small" />
                </SocialIconButton>
                <SocialIconButton size="small">
                  <TwitterIcon fontSize="small" />
                </SocialIconButton>
                <SocialIconButton size="small">
                  <InstagramIcon fontSize="small" />
                </SocialIconButton>
                <SocialIconButton size="small">
                  <LinkedInIcon fontSize="small" />
                </SocialIconButton>
              </Stack>
            </Grid>

            {/* Links - simplified to two columns */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={4}>
                <Grid item xs={6} sm={4}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 600,
                      mb: 2,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontSize: '0.75rem'
                    }}
                  >
                    Services
                  </Typography>
                  <FooterLink href="/parts">Parts Store</FooterLink>
                  <FooterLink href="/repair-guide">Repair Guides</FooterLink>
                  <FooterLink href="/issues">Community</FooterLink>
                  <FooterLink href="/smartsearch">AI Diagnostics</FooterLink>
                </Grid>
                
                <Grid item xs={6} sm={4}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 600,
                      mb: 2,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontSize: '0.75rem'
                    }}
                  >
                    Company
                  </Typography>
                  <FooterLink href="/about">About Us</FooterLink>
                  <FooterLink href="/contact">Contact</FooterLink>
                  <FooterLink href="/terms">Terms</FooterLink>
                  <FooterLink href="/privacy">Privacy</FooterLink>
                </Grid>
                
                <Grid item xs={6} sm={4}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: '#fff',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 600,
                      mb: 2,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontSize: '0.75rem'
                    }}
                  >
                    Contact
                  </Typography>
                  <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      support@warrili.com
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      +1 (555) 123-4567
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Copyright */}
          <Divider sx={{ mt: 6, mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.8rem'
              }}
            >
              © {new Date().getFullYear()} Warrili. All rights reserved.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Link 
                href="/terms" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)',
                  textDecoration: 'none',
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.8rem',
                  '&:hover': { color: '#FFD700' }
                }}
              >
                Terms
              </Link>
              <Link 
                href="/privacy" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)',
                  textDecoration: 'none',
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.8rem',
                  '&:hover': { color: '#FFD700' }
                }}
              >
                Privacy
              </Link>
              <Link 
                href="/cookies" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)',
                  textDecoration: 'none',
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.8rem',
                  '&:hover': { color: '#FFD700' }
                }}
              >
                Cookies
              </Link>
            </Box>
          </Box>
        </Container>
      </FooterContainer>
    </>
  );
};

export default SmartSearch; 